import type { ReactNode } from "react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

export type ConnectorItem = {
    key: string;
    type: "agent" | string;
    name: string;
    icon: ReactNode;
    description: string;
    category: string;
    status: "Enabled" | "Disabled" | string;
    version: "Early Access" | "Beta" | string;
    ribbon?: string;
    href: string;
};

interface ConnectorCardProps {
    item: ConnectorItem;
    className?: string;
}

export function ConnectorCard({ item, className }: ConnectorCardProps) {
    return (
        <Link to={item.href}>
            <Card className={cn("flex flex-col group relative cursor-pointer overflow-hidden rounded-md border border-border transition-all hover:border-primary hover:shadow-lg hover:shadow-purple-900/10", className)}>
                {/* Ribbon */}
                {item.ribbon && (
                    <div
                        className={`absolute right-0 top-0 z-10 px-3 py-1 text-xs font-semibold shadow-md ${
                            item.ribbon === "New" ? "bg-primary text-white" : "bg-blue-500 text-white"
                        }`}
                        style={{ borderBottomLeftRadius: "6px" }}
                    >
                        {item.ribbon}
                    </div>
                )}

                <CardContent className="p-5 flex-grow">
                    <div className="mb-4 flex items-start justify-between">
                        <div className="flex items-start gap-3">
                            <div className="rounded-lg bg-primary p-3 text-white transition-colors group-hover:bg-primary/80">
                                {item.icon}
                            </div>
                            <div className="flex flex-col mt-[3px] gap-0.5">
                                <div className="font-medium leading-none">{item.name}</div>
                                <div className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                                    {item.description}
                                </div>
                            </div>
                        </div>
                    </div>
                </CardContent>

                <CardFooter className="flex items-center justify-between p-5 pt-0">
                    <Badge
                        variant="outline"
                        className={`${item.type === "agent" ? "text-indigo-500" : "text-teal-500"}`}
                    >
                        {item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                    </Badge>

                    <div className="flex items-center space-x-3">
                        {/* Status indicator */}
                        <div className="flex items-center space-x-1.5">
                            <div
                                className={`h-2 w-2 rounded-full ${
                                    item.status === "Enabled"
                                        ? "bg-green-500"
                                        : item.status === "Disabled"
                                          ? "bg-gray-400"
                                          : "bg-red-500"
                                }`}
                            ></div>
                            <span className="text-xs text-muted-foreground">{item.status}</span>
                        </div>

                        {/* Version tag */}
                        <div className="text-xs">
                            <span
                                className={`${
                                    item.version === "Early Access"
                                        ? "text-purple-600"
                                        : item.version === "Beta"
                                          ? "text-blue-600"
                                          : "text-emerald-600"
                                }`}
                            >
                                {item.version}
                            </span>
                        </div>
                    </div>
                </CardFooter>
            </Card>
        </Link>
    );
}
